import React from "react";

export default function LabelPrintHistory({ history: h }) {
  const border = { borderColor: "var(--fb-border)" };
  return (
    <section className="space-y-3 text-sm" aria-label="Label print history" data-testid="avery-print-history">
      {h.pending && (
        <div className="rounded-xl border p-3 space-y-2" style={border} data-testid="avery-print-confirmation" aria-live="polite">
          <p className="font-semibold">Did these {h.pending.label_count} labels print successfully?</p>
          <p>{h.pending.request_count} request(s), {h.pending.sheet_count} sheet(s), {h.pending.year} — {h.pending.list_type}.
            {" "}This confirms only the captured batch, not later changes.</p>
          <p className="text-xs break-words">
            {h.pending.recipients.map(r => `${r.household_id} / ${r.member_id}`).join(", ")}
          </p>
          <div className="flex flex-wrap gap-3">
            <button type="button" data-testid="avery-confirm-print" disabled={!!h.busy || !h.pending.attempted}
              onClick={h.confirm} className="fb-btn-primary px-3 py-2 rounded-lg disabled:opacity-50">
              {h.busy === "confirming" ? "Recording…" : "Yes, mark printed"}
            </button>
            <button type="button" data-testid="avery-cancel-print" disabled={!!h.busy}
              onClick={() => h.discard()} className="border px-3 py-2 rounded-lg" style={border}>Not printed / cancel</button>
          </div>
        </div>
      )}
      {h.message && <p role="status" data-testid="avery-history-message">{h.message}</p>}
      {h.actionError && <p role="alert" className="fb-status-error rounded-lg p-3" data-testid="avery-history-action-error">{h.actionError}</p>}
      <details>
        <summary className="font-semibold cursor-pointer">Shared print history</summary>
        <p className="text-xs my-2" style={{ color: "var(--fb-text-muted)" }}>
          Only staff-confirmed batches are recorded. Printing before tracking began is unknown.
          Use the Print button to record a batch; browser printing alone never marks requests printed.
        </p>
        {!h.loading && !h.error && !h.canRecord &&
          <p className="text-xs my-2">Read-only account or year: you can print and view history, but cannot record new batches.</p>}
        {h.loading && <p role="status">Loading print history…</p>}
        {h.error && <p role="alert" data-testid="avery-history-error">{h.error}</p>}
        {!h.loading && !h.error && (
          <div className="max-h-64 overflow-y-auto space-y-2" aria-label="Confirmed print batches" tabIndex={0}>
            {!h.history.length && <p>No confirmed print batches on this page.</p>}
            {h.history.map(batch => (
              <article key={batch.id} className="rounded-lg border p-3 space-y-1" style={border} data-testid="avery-history-batch">
                <p className="font-semibold">{new Date(batch.confirmed_at).toLocaleString()} — {batch.staff || "Staff"}</p>
                <p>{batch.request_count} request(s) · {batch.label_count} labels · {batch.sheet_count} sheet(s)</p>
                <p className="text-xs">
                  Avery {batch.settings.product} · Start {batch.settings.start} · {batch.settings.orientation}
                  {batch.settings.items_per_tag ? ` · ${batch.settings.items_per_tag} toy items per tag` : ""}
                  {" · "}{batch.settings.gender_labels === "boy_girl" ? "Boy/Girl" : "Standard gender"}
                  {" · "}{batch.settings.size_labels === "abbreviated" ? "Abbreviated sizes" : "Full sizes"}
                  {" · "}{batch.settings.id_labels === "short" ? "Short IDs" : "Standard IDs"}
                </p>
                {batch.list_type === "clothing" && <p className="text-xs">
                  Size lines: {["pant", "shirt", "pj"].filter(key => batch.settings.sizes?.[key]).join(", ") || "None"}
                </p>}
                <details>
                  <summary className="cursor-pointer text-xs">Household / member IDs</summary>
                  <p className="text-xs break-words max-h-28 overflow-y-auto mt-1">
                    {batch.recipients.map(r => `${r.household_id} / ${r.member_id}`).join(", ")}
                  </p>
                </details>
              </article>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button type="button" onClick={h.refresh} disabled={h.loading} className="underline" data-testid="avery-reload-history">Reload history</button>
          <button type="button" onClick={() => h.setPage(p => Math.max(0, p - 1))}
            disabled={h.loading || h.page === 0} className="underline disabled:opacity-40">Previous history</button>
          <span>Page {h.page + 1}</span>
          <button type="button" onClick={() => h.setPage(p => p + 1)}
            disabled={h.loading || !h.hasMore} className="underline disabled:opacity-40">Next history</button>
        </div>
      </details>
      {h.error && <button type="button" onClick={h.refresh} disabled={h.loading} className="underline">Retry print history</button>}
    </section>
  );
}
